import { api } from "@/lib/api/client";

export type IntegrationReadinessStatus = "ready" | "blocked" | "unknown";

export interface ProviderRecoveryDestination {
  kind: "provider_hosted";
  action: "open_drive" | "open_zoom_recordings";
  url: string;
  label: string;
}

export interface IntegrationRecovery {
  prerequisite_id: string;
  code: string;
  owner: string;
  next_action: string;
  destination?: ProviderRecoveryDestination;
}

export interface IntegrationCapabilityReadiness {
  capability_id: string;
  label: string;
  family: string;
  credential_mode: string;
  delivery_status: "planned" | "existing_bounded" | "reference_adapter";
  adapter: string;
  commands: string[];
  action_available: boolean;
  status: IntegrationReadinessStatus;
  blocked_dependency_ids: string[];
  acceptance_requirements: string[];
  recovery: IntegrationRecovery[];
}

export interface IntegrationLifecycleEvidence {
  id: string;
  capability_id: string;
  artifact_key: string;
  provider_resource_id: string | null;
  retention: string;
  state: string;
  evidence_code: string | null;
  created_at: string;
  updated_at: string;
}

export interface CourseIntegrationReadiness {
  tenant_id: string;
  actor_id: string;
  course_id: string;
  observed_at: string;
  capabilities: IntegrationCapabilityReadiness[];
  lifecycle: IntegrationLifecycleEvidence[];
}

export function integrationReadinessQueryKey(courseId: string): readonly unknown[] {
  return ["course-integration-readiness", courseId];
}

const DESTINATION_CONTRACT = {
  // No destination opens Google Classroom (ADR 0026 as amended 2026-10-19).
  open_drive: {
    host: "drive.google.com",
    capabilities: new Set(["google.drive.selection"]),
  },
  open_zoom_recordings: {
    host: "zoom.us",
    capabilities: new Set(["zoom.recordings"]),
  },
} as const;

export function isSupportedProviderDestination(
  capabilityId: string,
  destination: ProviderRecoveryDestination,
): boolean {
  if (destination.kind !== "provider_hosted") return false;
  const contract = DESTINATION_CONTRACT[destination.action];
  if (!contract || !contract.capabilities.has(capabilityId)) return false;
  try {
    const url = new URL(destination.url);
    return url.protocol === "https:" && url.hostname === contract.host;
  } catch {
    return false;
  }
}

export const integrationsApi = {
  getCourseReadiness: (courseId: string) =>
    api.get<CourseIntegrationReadiness>(`/courses/${courseId}/integrations/readiness`),
};
